import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAccount } from 'wagmi';
import { FiAward, FiStar, FiShield, FiZap, FiTrendingUp, FiUsers } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const OnchainBadges = ({ profile, theme }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBadges();
  }, [profile]);

  const loadBadges = async () => {
    setLoading(true);
    try {
      // Mock badges - replace with actual onchain verification
      const mockBadges = [
        { id: 'early-creator', name: 'Early Creator', description: 'One of the first 1000 creators on HubID', icon: FiStar, color: 'bg-yellow-500', textColor: 'text-yellow-600', borderColor: 'border-yellow-500', rarity: 'legendary', earned: true, date: '2024-01-15' },
        { id: 'verified-creator', name: 'Verified Creator', description: 'Verified identity and social presence', icon: FiShield, color: 'bg-blue-500', textColor: 'text-blue-600', borderColor: 'border-blue-500', rarity: 'epic', earned: profile && profile.is_verified, date: profile?.verified_date },
        { id: 'top-creator', name: 'Top Creator', description: 'Top 10% creators by profile views', icon: FiTrendingUp, color: 'bg-purple-500', textColor: 'text-purple-600', borderColor: 'border-purple-500', rarity: 'epic', earned: Math.random() > 0.7, date: '2024-03-10' }, // Simulate 30% chance
        { id: 'community-builder', name: 'Community Builder', description: 'Built a thriving community of 1000+ followers', icon: FiUsers, color: 'bg-green-500', textColor: 'text-green-600', borderColor: 'border-green-500', rarity: 'rare', earned: Math.random() > 0.5, date: '2024-02-20' }, // Simulate 50% chance
        { id: 'nft-collector', name: 'NFT Collector', description: 'Owns 10+ verified NFTs', icon: FiAward, color: 'bg-indigo-500', textColor: 'text-indigo-600', borderColor: 'border-indigo-500', rarity: 'rare', earned: Math.random() > 0.6, date: '2024-04-05' }, // Simulate 40% chance
        { id: 'power-user', name: 'Power User', description: 'Active user for 6+ months', icon: FiZap, color: 'bg-red-500', textColor: 'text-red-600', borderColor: 'border-red-500', rarity: 'common', earned: true, date: '2024-01-01' }
      ];
      setBadges(mockBadges);
    } catch (error) {
      console.error('Error loading badges:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getRarityColor = (rarity) => {
    const colors = {
      legendary: 'from-yellow-400 to-orange-400',
      epic: 'from-purple-400 to-pink-400',
      rare: 'from-blue-400 to-cyan-400',
      common: 'from-gray-400 to-gray-500',
    };
    return colors[rarity] || colors.common;
  };
  
  const getRarityGradient = (rarity) => {
    const gradients = {
      legendary: 'bg-gradient-to-br from-yellow-100 to-orange-100',
      epic: 'bg-gradient-to-br from-purple-100 to-pink-100',
      rare: 'bg-gradient-to-br from-blue-100 to-cyan-100',
      common: 'bg-gradient-to-br from-gray-100 to-gray-200',
    };
    return gradients[rarity] || gradients.common;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const earnedBadges = badges.filter(badge => badge.earned);
  const totalBadges = badges.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border ${theme.border} ${theme.bg} overflow-hidden`}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-xl font-bold ${theme.text}`}>
            Onchain Badges
          </h3>
          <div className="flex items-center space-x-2">
            <span className={`text-sm ${theme.subtext}`}>Progress:</span>
            <span className={`font-bold ${theme.accent}`}>
              {earnedBadges.length}/{totalBadges}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className={`w-full h-2 ${theme.bg} ${theme.border} border rounded-full mb-6 overflow-hidden`}>
          <div
            className={`h-full bg-gradient-to-r ${getRarityColor('epic')} transition-all duration-500`}
            style={{ width: `${(earnedBadges.length / totalBadges) * 100}%` }}
          ></div>
        </div>

        {/* Earned Badges */}
        {earnedBadges.length > 0 && (
          <div className="mb-8">
            <h4 className={`font-semibold ${theme.text} mb-4`}>Earned Badges</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {earnedBadges.map((badge, index) => (
                <motion.div
                  key={badge.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`relative p-4 rounded-xl border-2 ${badge.borderColor} ${getRarityGradient(badge.rarity)} overflow-hidden group hover:shadow-lg transition-all cursor-pointer`}
                >
                  {/* Rarity Badge */}
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded-full bg-gradient-to-r ${getRarityColor(badge.rarity)} text-white text-xs font-bold uppercase`}>
                    {badge.rarity}
                  </div>
                  {/* Badge Icon */}
                  <div className="flex justify-center mb-3">
                    <div className={`w-12 h-12 ${badge.color} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <SafeIcon icon={badge.icon} className="text-white text-xl" />
                    </div>
                  </div>
                  {/* Badge Info */}
                  <div className="text-center">
                    <h5 className={`font-semibold ${theme.text} mb-1`}>{badge.name}</h5>
                    <p className={`text-xs ${theme.subtext} leading-relaxed`}>
                      {badge.description}
                    </p>
                    {badge.date && (
                      <p className={`text-xs ${theme.subtext} mt-2 opacity-75`}>
                        Earned {new Date(badge.date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {/* Shimmer effect for legendary badges */}
                  {badge.rarity === 'legendary' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 -skew-x-12 group-hover:translate-x-full transition-transform duration-1000"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Locked Badges */}
        <div>
          <h4 className={`font-semibold ${theme.text} mb-4`}>Locked Badges</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {badges.filter(badge => !badge.earned).map((badge, index) => (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`relative p-4 rounded-xl border-2 ${theme.border} ${theme.bg} opacity-60 cursor-not-allowed`}
              >
                {/* Lock overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-10 rounded-xl flex items-center justify-center">
                  <div className="w-8 h-8 bg-gray-800 bg-opacity-50 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiShield} className="text-white text-sm" />
                  </div>
                </div>
                {/* Badge Icon (grayed out) */}
                <div className="flex justify-center mb-3">
                  <div className={`w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center`}>
                    <SafeIcon icon={badge.icon} className="text-white text-xl opacity-50" />
                  </div>
                </div>
                {/* Badge Info */}
                <div className="text-center">
                  <h5 className={`font-semibold ${theme.text} mb-1`}>{badge.name}</h5>
                  <p className={`text-xs ${theme.subtext} opacity-75`}>
                    {badge.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Achievement Stats */}
        <div className={`mt-8 p-4 rounded-lg ${theme.bg} ${theme.border} border`}>
          <h4 className={`font-semibold ${theme.text} mb-3`}>Achievement Stats</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.accent}`}>{earnedBadges.length}</div>
              <div className={`text-xs ${theme.subtext}`}>Badges Earned</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.accent}`}>{badges.filter(b => b.rarity === 'legendary' && b.earned).length}</div>
              <div className={`text-xs ${theme.subtext}`}>Legendary</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.accent}`}>{badges.filter(b => b.rarity === 'epic' && b.earned).length}</div>
              <div className={`text-xs ${theme.subtext}`}>Epic</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.accent}`}>{Math.round((earnedBadges.length / totalBadges) * 100)}%</div>
              <div className={`text-xs ${theme.subtext}`}>Complete</div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default OnchainBadges;